import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock, FileCode, Rocket } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const ADMIN_EMAIL = "stonesargent@yahoo.com";

interface BuildRow {
  id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
  file_count: number;
  published: boolean;
  build_time_seconds: number | null;
  error_message: string | null;
}

interface TimelineEvent {
  id: string;
  action: string;
  description: string | null;
  created_at: string;
  project_id: string;
  project_name: string;
  metadata: Record<string, unknown> | null;
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("builds");

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth?redirect=/admin");
    }
    if (!authLoading && user && !isAdmin) {
      navigate("/");
    }
  }, [authLoading, user, isAdmin, navigate]);

  // Last 50 builds with file counts
  const { data: builds, isLoading: buildsLoading, refetch: refetchBuilds } = useQuery({
    queryKey: ["admin-builds"],
    queryFn: async () => {
      // Get projects with their latest snapshot for file count
      const { data: projects, error: projError } = await supabase
        .from("code_projects")
        .select("id, name, status, created_at, updated_at")
        .order("created_at", { ascending: false })
        .limit(50);

      if (projError) throw projError;

      // Get snapshots for file counts
      const projectIds = projects?.map((p) => p.id) || [];
      const { data: snapshots } = await supabase
        .from("project_snapshots")
        .select("project_id, files_snapshot, created_at")
        .in("project_id", projectIds)
        .order("created_at", { ascending: false });

      // Get timeline events for publish status and errors
      const { data: timeline } = await supabase
        .from("project_build_timeline")
        .select("project_id, action, description, metadata, created_at")
        .in("project_id", projectIds)
        .order("created_at", { ascending: false });

      // Get builder_messages for errors
      const { data: messages } = await supabase
        .from("builder_messages")
        .select("project_id, error_summary, status, created_at")
        .in("project_id", projectIds)
        .not("error_summary", "is", null);

      // Build the combined data
      const result: BuildRow[] = (projects || []).map((p) => {
        // Latest snapshot for this project
        const projectSnapshots = snapshots?.filter((s) => s.project_id === p.id) || [];
        const latestSnapshot = projectSnapshots[0];
        const fileCount = latestSnapshot
          ? (Array.isArray(latestSnapshot.files_snapshot)
              ? latestSnapshot.files_snapshot.length
              : Object.keys(latestSnapshot.files_snapshot as Record<string, unknown>).length)
          : 0;

        // Check for deploy action
        const projectTimeline = timeline?.filter((t) => t.project_id === p.id) || [];
        const deployEvent = projectTimeline.find((t) =>
          t.action.toLowerCase().includes("deploy") ||
          t.action.toLowerCase().includes("publish")
        );

        // Get earliest and latest events for build time
        const firstEvent = projectTimeline[projectTimeline.length - 1];
        const lastEvent = projectTimeline[0];
        const buildTime = firstEvent && lastEvent
          ? Math.round((new Date(lastEvent.created_at).getTime() - new Date(firstEvent.created_at).getTime()) / 1000)
          : null;

        // Get error message
        const projectErrors = messages?.filter((m) => m.project_id === p.id) || [];
        const latestError = projectErrors[0];

        return {
          id: p.id,
          name: p.name,
          status: p.status,
          created_at: p.created_at,
          updated_at: p.updated_at,
          file_count: fileCount,
          published: !!deployEvent,
          build_time_seconds: buildTime,
          error_message: latestError?.error_summary || null,
        };
      });

      return result;
    },
    enabled: isAdmin,
  });

  // Failed builds
  const failedBuilds = builds?.filter((b) => b.status === "error" || b.error_message) || [];

  // Deploy events from timeline
  const { data: deployEvents, isLoading: deployLoading, refetch: refetchDeploy } = useQuery({
    queryKey: ["admin-deploy-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_build_timeline")
        .select(`
          id,
          action,
          description,
          created_at,
          project_id,
          metadata
        `)
        .or("action.ilike.%deploy%,action.ilike.%publish%")
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;

      // Get project names
      const projectIds = [...new Set(data?.map((d) => d.project_id) || [])];
      const { data: projects } = await supabase
        .from("code_projects")
        .select("id, name")
        .in("id", projectIds);

      const projectMap = new Map(projects?.map((p) => [p.id, p.name]) || []);

      return (data || []).map((d) => ({
        ...d,
        project_name: projectMap.get(d.project_id) || "Unknown",
        metadata: d.metadata as Record<string, unknown> | null,
      })) as TimelineEvent[];
    },
    enabled: isAdmin,
  });

  // Schema events
  const { data: schemaEvents, isLoading: schemaLoading, refetch: refetchSchema } = useQuery({
    queryKey: ["admin-schema-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_build_timeline")
        .select(`
          id,
          action,
          description,
          created_at,
          project_id,
          metadata
        `)
        .or("action.ilike.%schema%,action.ilike.%migration%,action.ilike.%database%")
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;

      const projectIds = [...new Set(data?.map((d) => d.project_id) || [])];
      const { data: projects } = await supabase
        .from("code_projects")
        .select("id, name")
        .in("id", projectIds);

      const projectMap = new Map(projects?.map((p) => [p.id, p.name]) || []);

      return (data || []).map((d) => ({
        ...d,
        project_name: projectMap.get(d.project_id) || "Unknown",
        metadata: d.metadata as Record<string, unknown> | null,
      })) as TimelineEvent[];
    },
    enabled: isAdmin,
  });

  // Calculate stats
  const deploySuccessCount = deployEvents?.filter((e) =>
    !e.description?.toLowerCase().includes("fail") &&
    !e.description?.toLowerCase().includes("error")
  ).length || 0;
  const deployFailCount = deployEvents?.filter((e) =>
    e.description?.toLowerCase().includes("fail") ||
    e.description?.toLowerCase().includes("error")
  ).length || 0;
  const deployTotal = deploySuccessCount + deployFailCount;
  const deploySuccessRate = deployTotal > 0 ? Math.round((deploySuccessCount / deployTotal) * 100) : 0;

  const schemaSuccessCount = schemaEvents?.filter((e) =>
    !e.description?.toLowerCase().includes("fail") &&
    !e.description?.toLowerCase().includes("error")
  ).length || 0;
  const schemaFailCount = schemaEvents?.filter((e) =>
    e.description?.toLowerCase().includes("fail") ||
    e.description?.toLowerCase().includes("error")
  ).length || 0;
  const schemaTotal = schemaSuccessCount + schemaFailCount;
  const schemaSuccessRate = schemaTotal > 0 ? Math.round((schemaSuccessCount / schemaTotal) * 100) : 0;

  const refetchAll = () => {
    refetchBuilds();
    refetchDeploy();
    refetchSchema();
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#080810] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#080810] flex items-center justify-center">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Access Denied</h3>
            <p className="text-slate-400">Admin access required.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLoading = buildsLoading || deployLoading || schemaLoading;

  return (
    <div className="min-h-screen bg-[#080810] text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-slate-400 mt-1">DayOne build monitoring</p>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={refetchAll}
            disabled={isLoading}
            className="border-slate-700 hover:bg-slate-800"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Total Builds</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{builds?.length || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Failed Builds</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-400">{failedBuilds.length}</div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Deploy Success Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-400">{deploySuccessRate}%</div>
              <div className="text-xs text-slate-500">{deploySuccessCount}/{deployTotal}</div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Schema Success Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">{schemaSuccessRate}%</div>
              <div className="text-xs text-slate-500">{schemaSuccessCount}/{schemaTotal}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border-slate-800">
            <TabsTrigger value="builds" className="data-[state=active]:bg-indigo-600">
              Builds
            </TabsTrigger>
            <TabsTrigger value="errors" className="data-[state=active]:bg-indigo-600">
              Errors ({failedBuilds.length})
            </TabsTrigger>
            <TabsTrigger value="deploys" className="data-[state=active]:bg-indigo-600">
              Deploys
            </TabsTrigger>
            <TabsTrigger value="schemas" className="data-[state=active]:bg-indigo-600">
              Schema
            </TabsTrigger>
          </TabsList>

          {/* Builds Tab */}
          <TabsContent value="builds" className="mt-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Last 50 Builds</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left py-3 px-2 text-slate-400">Project</th>
                        <th className="text-left py-3 px-2 text-slate-400">Status</th>
                        <th className="text-left py-3 px-2 text-slate-400">Files</th>
                        <th className="text-left py-3 px-2 text-slate-400">Build Time</th>
                        <th className="text-left py-3 px-2 text-slate-400">Published</th>
                        <th className="text-left py-3 px-2 text-slate-400">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {builds?.map((build) => (
                        <tr key={build.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-3 px-2">
                            <div className="font-medium text-white truncate max-w-[200px]">{build.name}</div>
                            <div className="text-xs text-slate-500 font-mono">{build.id.slice(0, 8)}</div>
                          </td>
                          <td className="py-3 px-2">
                            <Badge
                              variant={build.status === "error" ? "destructive" : build.status === "completed" ? "default" : "secondary"}
                              className={build.status === "completed" ? "bg-emerald-600" : ""}
                            >
                              {build.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-1 text-slate-300">
                              <FileCode className="h-3 w-3" />
                              {build.file_count}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-slate-300">
                            {build.build_time_seconds ? (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {build.build_time_seconds < 60
                                  ? `${build.build_time_seconds}s`
                                  : `${Math.floor(build.build_time_seconds / 60)}m ${build.build_time_seconds % 60}s`
                                }
                              </div>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {build.published ? (
                              <Rocket className="h-4 w-4 text-emerald-400" />
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-slate-400 text-xs">
                            {formatDistanceToNow(new Date(build.created_at), { addSuffix: true })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Errors Tab */}
          <TabsContent value="errors" className="mt-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  Failed Builds
                </CardTitle>
              </CardHeader>
              <CardContent>
                {failedBuilds.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-emerald-500" />
                    No failed builds
                  </div>
                ) : (
                  <div className="space-y-4">
                    {failedBuilds.map((build) => (
                      <div key={build.id} className="bg-slate-800/50 rounded-lg p-4 border border-red-900/30">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-white">{build.name}</div>
                            <div className="text-xs text-slate-500 font-mono">{build.id}</div>
                          </div>
                          <div className="text-xs text-slate-400">
                            {formatDistanceToNow(new Date(build.created_at), { addSuffix: true })}
                          </div>
                        </div>
                        {build.error_message && (
                          <div className="mt-2 p-2 bg-red-950/30 rounded text-sm text-red-300 font-mono">
                            {build.error_message}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deploys Tab */}
          <TabsContent value="deploys" className="mt-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-indigo-400" />
                  Deploy Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {deployEvents?.slice(0, 30).map((event) => {
                    const isError = event.description?.toLowerCase().includes("fail") ||
                                   event.description?.toLowerCase().includes("error");
                    return (
                      <div key={event.id} className="flex items-center gap-3 py-2 border-b border-slate-800/50">
                        {isError ? (
                          <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{event.project_name}</div>
                          <div className="text-xs text-slate-500">{event.action}</div>
                        </div>
                        {event.description && (
                          <div className="text-xs text-slate-400 truncate max-w-[200px]">
                            {event.description}
                          </div>
                        )}
                        <div className="text-xs text-slate-500 shrink-0">
                          {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                        </div>
                      </div>
                    );
                  })}
                  {(!deployEvents || deployEvents.length === 0) && (
                    <div className="text-center py-8 text-slate-400">No deploy events found</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schema Tab */}
          <TabsContent value="schemas" className="mt-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  Apply-Schema Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {schemaEvents?.slice(0, 30).map((event) => {
                    const isError = event.description?.toLowerCase().includes("fail") ||
                                   event.description?.toLowerCase().includes("error");
                    return (
                      <div key={event.id} className="flex items-center gap-3 py-2 border-b border-slate-800/50">
                        {isError ? (
                          <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{event.project_name}</div>
                          <div className="text-xs text-slate-500">{event.action}</div>
                        </div>
                        {event.description && (
                          <div className="text-xs text-slate-400 truncate max-w-[200px]">
                            {event.description}
                          </div>
                        )}
                        <div className="text-xs text-slate-500 shrink-0">
                          {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                        </div>
                      </div>
                    );
                  })}
                  {(!schemaEvents || schemaEvents.length === 0) && (
                    <div className="text-center py-8 text-slate-400">No schema events found</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
